import { colorToRGBA } from "src/utils/colorToRgba";
import React, { useRef } from "react";
import { Transition } from "src/utils";
import styled from "styled-components";
import { Icon } from "../Icon/Icon";
import { Flex } from "../Box/Box";
import { Color } from "../Color/Color";
import { Text } from "../Text/Text";
import { useHoverClickable } from "src/utils/hoverClickable";
import { Activity } from "../Activity/Activity";

const Container = styled(Flex)<{ isDisabled?: boolean }>`
  align-items: center;
  gap: 8px;
  cursor: pointer;
  pointer-events: ${({ isDisabled }) => isDisabled && "none"};
  user-select: none;
`;

const Background = styled(Flex)<{
  backgroundColor: string;
}>`
  min-width: 30px;
  width: 30px;
  min-height: 18px;
  max-height: 18px;
  align-items: center;
  padding: 3px;
  border-radius: 18px;
  transition: background-color ${Transition.fast};
  background-color: ${({ backgroundColor }) => backgroundColor};
`;

const Handle = styled(Flex)<{ handleColor: string; isChecked: boolean }>`
  width: 12px;
  height: 12px;
  justify-content: center;
  align-items: center;
  border-radius: 50%;
  background-color: ${({ handleColor }) => handleColor};
  transition: transform ${Transition.fast};
  transform: translateX(${({ isChecked }) => (isChecked ? "100%" : "0%")});
`;

interface Props {
  value: boolean;
  setValue: (value: boolean) => void;
  offLabel?: string;
  onLabel?: string;
  label?: string;
  isDisabled?: boolean;
}

export const Toggle = ({ value, setValue, offLabel, onLabel, label, isDisabled }: Props) => {
  const refToggle = useRef<HTMLDivElement>(null);
  const { isHovered, isActive } = useHoverClickable(refToggle);
  const isChecked = (!value && isActive) || (value && !isActive);

  const backgroundColor =
    (isDisabled && Color.lightGrayHover) ||
    (!value && isActive && colorToRGBA(Color.primary, 0.7)) ||
    (!value && isHovered && Color.spaceGrayHover) ||
    (value && isActive && colorToRGBA(Color.primary, 0.7)) ||
    (value && Color.primary) ||
    Color.spaceGray;

  const handleColor = (isDisabled && Color.lightGray) || Color.white;

  const checkmarkColor = (isDisabled && Color.textDisabled) || Color.primary;

  const toggleChecked = () => {
    setValue(!value);
  };

  return (
    <Container ref={refToggle} isDisabled={isDisabled} onClick={toggleChecked}>
      <Activity isVisible={offLabel !== undefined}>
        <Flex opacity={value === false ? 1 : 0} transition={`opacity ${Transition.fast}`}>
          <Text variant="small1" color={isDisabled ? Color.textDisabled : Color.textSecondary}>
            {offLabel}
          </Text>
        </Flex>
      </Activity>

      <Background backgroundColor={backgroundColor}>
        <Handle handleColor={handleColor} isChecked={isChecked}>
          <Flex
            width="100%"
            height="100%"
            justify="center"
            align="center"
            opacity={isChecked ? 1 : 0}
            transition={`opacity ${Transition.fast}`}
          >
            <Icon name="checkmark" size={12} color={checkmarkColor} />
          </Flex>
        </Handle>
      </Background>

      <Activity isVisible={onLabel !== undefined}>
        <Flex opacity={value === true ? 1 : 0} transition={`opacity ${Transition.fast}`}>
          <Text variant="small1" color={isDisabled ? Color.textDisabled : Color.textSecondary}>
            {onLabel}
          </Text>
        </Flex>
      </Activity>

      {label && (
        <Text
          variant="small2"
          whiteSpace="nowrap"
          color={isDisabled ? Color.textDisabled : Color.textSecondary}
        >
          {label}
        </Text>
      )}
    </Container>
  );
};
